import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { Btn, Input } from "@/components/ui-kit";
import { Languages, Lock, Mail, Activity } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const { t, toggle, lang } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    const res = await login(email, password);
    setBusy(false);
    if (res.success) navigate("/");
    else setErr(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Left side - branding */}
      <div className="hidden lg:flex flex-col justify-between bg-blue-700 text-white p-12 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-blue-600/40 blur-3xl"></div>
        <div className="absolute bottom-0 -left-20 w-80 h-80 rounded-full bg-sky-500/20 blur-3xl"></div>
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-md bg-white text-blue-700 grid place-items-center font-extrabold text-2xl">
              Px
            </div>
            <div>
              <div className="text-xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>
                {t("appName")}
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-blue-200 font-bold">
                {t("appTagline")}
              </div>
            </div>
          </div>
        </div>
        <div className="relative z-10 space-y-6">
          <h2 className="text-4xl xl:text-5xl font-extrabold leading-[1.05] tracking-tighter" style={{ fontFamily: "Manrope" }}>
            Pharmaceutical Field Force.<br />
            <span className="text-sky-300">Operational Intelligence.</span>
          </h2>
          <p className="text-blue-100 text-base leading-relaxed max-w-md">
            Centralized doctor & pharmacy intelligence, route execution, market visibility, collections,
            inventory and cost control — built for Egyptian OTC distribution teams.
          </p>
          <div className="grid grid-cols-3 gap-4 max-w-md pt-4">
            {[
              { v: "12", l: "Modules" },
              { v: "RTL", l: "Bilingual" },
              { v: "GPS", l: "Visit Track" },
            ].map((s) => (
              <div key={s.l} className="border-s-2 border-sky-300/60 ps-3">
                <div className="text-2xl font-extrabold tracking-tighter">{s.v}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-blue-200">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs text-blue-200">
          Egypt · Gharbia · Kafr El Sheikh
        </div>
      </div>

      {/* Right side - form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="flex justify-end mb-8">
            <button
              onClick={toggle}
              data-testid="lang-toggle-login"
              className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-blue-700"
            >
              <Languages className="w-4 h-4" />
              {lang === "en" ? "العربية" : "English"}
            </button>
          </div>
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-md bg-blue-700 text-white grid place-items-center font-extrabold">Px</div>
            <div>
              <div className="font-extrabold text-slate-900">{t("appName")}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">{t("appTagline")}</div>
            </div>
          </div>

          <h1 className="text-3xl font-extrabold tracking-tighter text-slate-900 mb-2" style={{ fontFamily: "Manrope" }}>
            {t("welcomeBack")}
          </h1>
          <p className="text-sm text-slate-500 mb-8">{t("enterCredentials")}</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div className="relative">
              <Mail className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none mt-3" />
              <Input
                label={t("email")}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@pharmacrm.com"
                required
                data-testid="login-email"
                className="ps-9"
              />
            </div>
            <div className="relative">
              <Lock className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none mt-3" />
              <Input
                label={t("password")}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="login-password"
                className="ps-9"
              />
            </div>

            {err && (
              <div className="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" data-testid="login-error">
                {err}
              </div>
            )}

            <Btn type="submit" className="w-full" disabled={busy} data-testid="login-submit">
              {busy ? t("loading") : t("signIn")}
            </Btn>
          </form>

          <div className="mt-3 text-center">
            <a href="/forgot-password" className="text-sm text-blue-700 hover:underline" data-testid="forgot-link">Forgot password?</a>
          </div>
        </div>
      </div>
    </div>
  );
}
